import { useState, useEffect, useRef } from "react";
import axios from "axios";
import Layout from "../components/Layout";
import { StepUpDialog } from "../components/StepUpDialog";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Skeleton } from "../components/ui/skeleton";
import {
  ArrowDownLeft,
  ArrowUpRight,
  DollarSign,
  Send,
  Wallet,
  Clock,
  CheckCircle2,
  XCircle,
  Landmark,
  Users,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  pending: { color: "bg-amber-100 text-amber-800", icon: Clock },
  processing: { color: "bg-blue-100 text-blue-800", icon: Clock },
  posted: { color: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  settled: { color: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  completed: { color: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  failed: { color: "bg-red-100 text-red-800", icon: XCircle },
  returned: { color: "bg-red-100 text-red-800", icon: XCircle },
};

const Payouts = () => {
  const { user } = useAuth();
  const [ledger, setLedger] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isFundDialogOpen, setIsFundDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stepUpOpen, setStepUpOpen] = useState(false);
  const pendingActionRef = useRef(null);
  const [form, setForm] = useState({
    recipient_id: "",
    amount: "",
    description: "Invoice Payout",
    network: "ach",
  });
  const [fundForm, setFundForm] = useState({ amount: "", description: "Ledger Funding" });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ledgerRes, payoutsRes, txRes, recipientsRes] = await Promise.all([
        axios.get(`${API_URL}/api/ledger/balance`),
        axios.get(`${API_URL}/api/payouts`),
        axios.get(`${API_URL}/api/ledger/transactions`),
        axios.get(`${API_URL}/api/payouts/recipients/list`),
      ]);
      setLedger(ledgerRes.data);
      setPayouts(payoutsRes.data.payouts);
      setTransactions(txRes.data.transactions);
      setRecipients(recipientsRes.data.recipients);
    } catch (error) {
      toast.error("Failed to load payout data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAuthorizeAndExecute = async () => {
    if (!form.recipient_id || !form.amount) {
      toast.error("Pengagement select a recipient and enter an amount");
      return;
    }
    setIsSubmitting(true);
    try {
      // Step 1: Authorize
      const authRes = await axios.post(`${API_URL}/api/payouts/authorize`, form);
      toast.success(`Payout authorized: $${form.amount}`);

      // Step 2: Execute
      const execRes = await axios.post(`${API_URL}/api/payouts/execute`, {
        payout_id: authRes.data.payout_id,
      });
      toast.success(`Payout executed to ${execRes.data.recipient} — Status: ${execRes.data.status}`);

      setIsDialogOpen(false);
      setForm({ recipient_id: "", amount: "", description: "Invoice Payout", network: "ach" });
      fetchData();
    } catch (error) {
      if (error.response?.data?.detail === "step_up_required") {
        pendingActionRef.current = handleAuthorizeAndExecute;
        setStepUpOpen(true);
        setIsSubmitting(false);
        return;
      }
      toast.error(error.response?.data?.detail || "Payout failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFundLedger = async () => {
    if (!fundForm.amount) { toast.error("Enter an amount"); return; }
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/ledger/fund`, fundForm);
      const sweepId = res.data?.plaid_sweep_id;
      if (sweepId) {
        toast.success(`$${fundForm.amount} deposited via Plaid Ledger`, { description: `Sweep: ${sweepId.slice(0, 8)}…` });
      } else {
        toast.success(`$${fundForm.amount} recorded in CPC Ledger`, { description: "Plaid sweep unavailable — using DB fallback" });
      }
      setIsFundDialogOpen(false);
      setFundForm({ amount: "", description: "Ledger Funding" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Funding failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (val) => {
    const num = typeof val === "string" ? parseFloat(val) : val;
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(num || 0);
  };

  const formatDate = (d) => {
    if (!d) return "-";
    return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-28" />)}
          </div>
          <Skeleton className="h-64" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="payouts-title">
              CPC Ledger & Payouts
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-stone-500">Fund ledger, send payouts to vendors and contractors</p>
              {ledger?.ledger_source === "plaid" ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-800" data-testid="plaid-ledger-live-badge">
                  <span className="w-1.5 h-1.5 bg-violet-600 rounded-full animate-pulse" />
                  Plaid Ledger · Live
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800" data-testid="plaid-ledger-fallback-badge">
                  DB Fallback
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setIsFundDialogOpen(true)}
              variant="outline"
              className="rounded-full px-5 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
              data-testid="fund-ledger-btn"
            >
              <DollarSign className="w-4 h-4 mr-1" />
              Add Funds
            </Button>
          </div>
        </div>

        {/* Ledger Stats */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card data-testid="stat-available">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone-500">Available</p>
                  <p className="text-2xl font-bold text-emerald-700">{formatCurrency(ledger?.available_balance)}</p>
                </div>
                <div className="w-11 h-11 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-emerald-700" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-funding">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone-500">Funded</p>
                  <p className="text-2xl font-bold text-violet-700">{formatCurrency(ledger?.total_funding)}</p>
                </div>
                <div className="w-11 h-11 bg-violet-100 rounded-xl flex items-center justify-center">
                  <Landmark className="w-5 h-5 text-violet-700" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-inflows">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone-500">Inflows</p>
                  <p className="text-2xl font-bold text-blue-700">{formatCurrency(ledger?.total_inflows)}</p>
                </div>
                <div className="w-11 h-11 bg-blue-100 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-blue-700" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-outflows">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone-500">Paid Out</p>
                  <p className="text-2xl font-bold text-stone-700">{formatCurrency(ledger?.total_outflows)}</p>
                </div>
                <div className="w-11 h-11 bg-stone-100 rounded-xl flex items-center justify-center">
                  <TrendingDown className="w-5 h-5 text-stone-700" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-pending">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone-500">Pending</p>
                  <p className="text-2xl font-bold text-amber-700">{formatCurrency(ledger?.pending_outflows)}</p>
                </div>
                <div className="w-11 h-11 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-700" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Payouts Table */}
        <Card data-testid="payouts-table-card">
          <CardHeader>
            <CardTitle style={{ fontFamily: "Manrope, sans-serif" }}>Payout History</CardTitle>
            <CardDescription>Credit transfers from CPC Ledger to recipients</CardDescription>
          </CardHeader>
          <CardContent>
            {payouts.length === 0 ? (
              <div className="text-center py-10 text-stone-400">
                <Send className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p>No payouts yet</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Network</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payouts.map((p) => {
                    const cfg = statusConfig[p.status] || statusConfig.pending;
                    return (
                      <TableRow key={p.payout_id} data-testid={`payout-row-${p.payout_id}`}>
                        <TableCell className="font-medium">
                          {p.recipient_name}
                          {p.is_direct && <span className="text-xs text-stone-400 block">ACH: ****{p.routing_mask?.slice(-4)} / {p.account_number_masked}</span>}
                        </TableCell>
                        <TableCell className="font-semibold text-emerald-700">{formatCurrency(p.amount)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs uppercase">{p.network}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${cfg.color} border-0`}>{p.status}</Badge>
                        </TableCell>
                        <TableCell className="text-stone-500">{p.description}</TableCell>
                        <TableCell className="text-stone-400 text-sm">{formatDate(p.created_at)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Ledger Transactions */}
        <Card data-testid="ledger-transactions-card">
          <CardHeader>
            <CardTitle style={{ fontFamily: "Manrope, sans-serif" }}>Ledger Transactions</CardTitle>
            <CardDescription>All fund movements through CPC Ledger</CardDescription>
          </CardHeader>
          <CardContent>
            {transactions.length === 0 ? (
              <div className="text-center py-10 text-stone-400">
                <DollarSign className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p>No transactions yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {transactions.slice(0, 20).map((tx) => (
                  <div key={tx.id} className="flex items-center justify-between p-3 rounded-xl bg-stone-50 hover:bg-stone-100 transition-colors" data-testid={`tx-${tx.id}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                        tx.type === "funding" ? "bg-violet-100" : tx.type === "inflow" ? "bg-emerald-100" : "bg-red-100"
                      }`}>
                        {tx.type === "funding" ? (
                          <Landmark className="w-4 h-4 text-violet-700" />
                        ) : tx.type === "inflow" ? (
                          <ArrowDownLeft className="w-4 h-4 text-emerald-700" />
                        ) : (
                          <ArrowUpRight className="w-4 h-4 text-red-700" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-stone-900 text-sm">{tx.description}</p>
                        <p className="text-xs text-stone-400">{tx.counterparty} &middot; {tx.network.toUpperCase()}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-semibold text-sm ${
                        tx.type === "funding" ? "text-violet-700" : tx.type === "inflow" ? "text-emerald-700" : "text-red-700"
                      }`}>
                        {tx.type === "outflow" ? "-" : "+"}{formatCurrency(tx.amount)}
                      </p>
                      <p className="text-xs text-stone-400">{formatDate(tx.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* New Payout Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>New Payout</DialogTitle>
              <DialogDescription>
                Send funds from CPC Ledger to a vendor or employee
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Recipient</Label>
                <Select value={form.recipient_id} onValueChange={(v) => setForm({ ...form, recipient_id: v })}>
                  <SelectTrigger data-testid="payout-recipient-select">
                    <SelectValue placeholder="Select recipient" />
                  </SelectTrigger>
                  <SelectContent>
                    {recipients.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        <div className="flex items-center gap-2">
                          <span>{r.full_name}</span>
                          {r.bank_linked ? (
                            <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs">Bank Linked</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-700 border-0 text-xs">No Bank</Badge>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Amount ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  placeholder="1500.00"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  data-testid="payout-amount-input"
                />
                {ledger && (
                  <p className="text-xs text-stone-400 mt-1">Available: {formatCurrency(ledger.available_balance)}</p>
                )}
              </div>

              <div>
                <Label>Description</Label>
                <Input
                  placeholder="Invoice Payout - May"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="payout-description-input"
                />
              </div>

              <div>
                <Label>Network</Label>
                <Select value={form.network} onValueChange={(v) => setForm({ ...form, network: v })}>
                  <SelectTrigger data-testid="payout-network-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ach">ACH (1-3 business days)</SelectItem>
                    <SelectItem value="same-day-ach">Same-Day ACH</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                onClick={handleAuthorizeAndExecute}
                className="w-full bg-emerald-900 hover:bg-emerald-800 rounded-full"
                disabled={isSubmitting || !form.recipient_id || !form.amount}
                data-testid="execute-payout-btn"
              >
                {isSubmitting ? (
                  <Clock className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                {isSubmitting ? "Processing..." : `Send ${form.amount ? formatCurrency(form.amount) : "Payout"}`}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Fund Ledger Dialog */}
        <Dialog open={isFundDialogOpen} onOpenChange={setIsFundDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add Funds to CPC Ledger</DialogTitle>
              <DialogDescription>
                Transfer funds from CPC's linked bank account into the ledger
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Amount ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  placeholder="10000.00"
                  value={fundForm.amount}
                  onChange={(e) => setFundForm({ ...fundForm, amount: e.target.value })}
                  data-testid="fund-amount-input"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  placeholder="Ledger Funding - May"
                  value={fundForm.description}
                  onChange={(e) => setFundForm({ ...fundForm, description: e.target.value })}
                  data-testid="fund-description-input"
                />
              </div>
              <Button
                onClick={handleFundLedger}
                className="w-full bg-violet-700 hover:bg-violet-600 rounded-full"
                disabled={isSubmitting || !fundForm.amount}
                data-testid="confirm-fund-btn"
              >
                {isSubmitting ? <Clock className="w-4 h-4 mr-2 animate-spin" /> : <DollarSign className="w-4 h-4 mr-2" />}
                {isSubmitting ? "Processing..." : `Fund ${fundForm.amount ? formatCurrency(fundForm.amount) : "Ledger"}`}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <StepUpDialog
          open={stepUpOpen}
          onOpenChange={setStepUpOpen}
          onVerified={() => {
            const fn = pendingActionRef.current;
            pendingActionRef.current = null;
            if (fn) fn();
          }}
        />
      </div>
    </Layout>
  );
};

export default Payouts;
